import { useNotification } from "hooks/useNotification";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { User } from "types/user.type";
import instance from "./axiosInstance";
import userStore from "store/user.store";
import { Role } from "types/enums";
import { TransportSedelQueryKey } from "./transportSedel.api";
import { tokenStore } from "store/token.store";
export const USER_ENDPOINT = "user";

export enum UserQueryKey {
  GetOne = "get-user",
  GetMany = "getUsers",
}

interface GetUserListParams {
  roles?: Role[];
  includeDisabledUsers?: boolean;
}

export const useUsers = (options = {}, params: GetUserListParams = {}) =>
  useQuery({
    queryKey: [UserQueryKey.GetMany, params],
    queryFn: async (): Promise<User[]> => {
      const response = await instance.get(USER_ENDPOINT, { params });
      return response.data;
    },
    ...options,
  });

export const useToggleUserDisabled = () => {
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      isDisabled,
    }: {
      id: number;
      isDisabled: boolean;
    }): Promise<User> =>
      (await instance.patch(`${USER_ENDPOINT}/${id}/disabled`, { isDisabled }))
        .data,
    onSuccess: (_data, { isDisabled }) => {
      queryClient.invalidateQueries({ queryKey: [UserQueryKey.GetMany] });
      notification.info(
        isDisabled ? "Användaren avaktiverades" : "Användaren aktiverades",
      );
    },
    onError: () => {
      notification.error("Något gick fel, försök igen");
    },
  });
};

export const useUser = (id: number) =>
  useQuery({
    queryKey: [UserQueryKey.GetOne],
    queryFn: async (): Promise<User> => {
      const response = await instance.get(`${USER_ENDPOINT}/${id}`);
      return response.data;
    },
    enabled: id > 0,
  });

export const useCurrentUser = () => {
  // get user
  const { user } = userStore();
  const { data: serverUser, isLoading } = useUser(user?.id ?? -1);
  return { user: serverUser, isLoading };
};

export const useSignup = () => {
  return useMutation({
    mutationFn: async (payload: SignupDTO) => {
      const response = await instance.post(
        `${USER_ENDPOINT}/signup-by-code`,
        payload,
      );
      return response.data;
    },
  });
};

export const useMagicLinkLogin = () => {
  const { setUser } = userStore();

  return useMutation({
    mutationFn: async (payload: { code: string }) => {
      const response = await instance.post(
        `${USER_ENDPOINT}/magic-link/sign-in`,
        payload,
      );
      return response.data;
    },
    onSuccess: (data) => {
      setUser(data.user);
      tokenStore.setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpiresIn: data.accessTokenExpiresIn,
        refreshTokenExpirationDate: new Date(data.refreshTokenExpirationDate)
          .getTime()
          .toString(),
      });
      tokenStore.updateTokenSetTime();
    },
  });
};

export const useChangePassword = () => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: ChangePasswordDTO) => {
      return instance.put(`${USER_ENDPOINT}/change-password`, payload);
    },
    onSuccess: () => {
      notification.info(`Lösenordet ändrat`);
    },
    onError: () => {
      notification.error("Fel lösenord, försök igen");
    },
  });
};

// export const useUpdateUser = () => {
//   const notification = useNotification();
//   const queryClient = useQueryClient();
//   return useMutation(
//     async (payload: UpdateUserDTO) =>
//       await instance.put(`${ENDPOINT}`, payload),
//     {
//       onSuccess: () => {
//         notification.changesSaved();
//         queryClient.invalidateQueries([GET_USER_QK]);
//       },
//       onError: () => {
//         notification.error();
//       },
//     }
//   );
// };

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (payload: ForgotPasswordDTO): Promise<void> => {
      return instance.post(`${USER_ENDPOINT}/forgot-password`, payload);
    },
  });
};

export const useResetPassword = () => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: ResetPasswordDTO) => {
      return instance.put(`${USER_ENDPOINT}/reset-password`, payload);
    },
    onSuccess: () => {
      notification.info(`Lösenordet ändrat`);
    },
    onError: () => {
      notification.error("Något gick fel, försök igen");
    },
  });
};

export const useSetActiveVehicles = (userId: number) => {
  const notification = useNotification();
  const quertClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: number[]) => {
      return instance.put(`${USER_ENDPOINT}/${userId}/active-vehicles`, {
        vehicleIds: payload,
      });
    },
    onSuccess: () => {
      quertClient.invalidateQueries({
        queryKey: [UserQueryKey.GetOne],
      });
      quertClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });
      // notification.info(``);
    },
    onError: () => {
      notification.error("Något gick fel, försök igen");
    },
  });
};

export type SignupDTO = {
  code: string;
  password: string;
  name: string;
};

export type ChangePasswordDTO = {
  oldPassword: string;
  newPassword: string;
};

export type ResetPasswordDTO = {
  password: string;
  code: string;
};

export type ForgotPasswordDTO = {
  email: string;
};

export type UpdateUserDTO = {
  name: string;
};

export type PatchUserDto = {
  signature?: string | null;
  primaryVehicleUuid?: string | null;
  externalId?: string | null;
  // String of exactly 4 chars to set, null to clear, omit to leave unchanged.
  pinCode?: string | null;
};

export const patchUser = (id: number) => ({
  mutationFn: async (payload: PatchUserDto): Promise<User> =>
    (await instance.patch(`${USER_ENDPOINT}/${id}`, payload)).data,
});
